import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

/**
 * Hook to manage the "jump to bottom" button visibility in the chat
 *
 * When users scroll up to view older messages, they need a way to
 * quickly return to the latest messages at the "bottom" of the chat.
 *
 * This hook:
 * 1. Uses an intersection observer to track when the bottom of the chat is visible
 * 2. Shows a "jump to bottom" button when the user has scrolled up away from recent messages
 * 3. Hides the button when the user is already viewing the most recent messages
 *
 * Note: In flipped chat containers (newest at top), the "bottom" is actually
 * visually at the top of the container.
 *
 * @returns Object containing:
 *   - showJumpButton: Whether the jump button should be displayed
 *   - bottomRef: Ref to attach to the element at the "bottom" of the chat
 */
export function useJumpButton() {
  // Track whether to show the jump button
  const [showJumpButton, setShowJumpButton] = useState(false);

  // Create an intersection observer to detect when the "bottom" of the chat is visible
  const { ref: bottomRef, inView: isBottomVisible } = useInView({
    threshold: 0.1,
  });

  // Update button visibility based on whether the bottom is visible
  useEffect(() => {
    // Show the button when the bottom is NOT visible (user has scrolled up)
    // Hide the button when the bottom IS visible (user is at the latest messages)
    setShowJumpButton(!isBottomVisible);
  }, [isBottomVisible]);

  return {
    showJumpButton,
    bottomRef,
  };
}
